import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPackage } from 'react-icons/fi';
import { bubbleTeaAPI, BubbleTeaOrderResponse } from '../api/bubbletea';
import { formatCurrency } from '../utils/currency';
import { getStatusLabel } from '../utils/orderStatus';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  color: var(--text-color);
  margin-bottom: 1.5rem;
`;

const OrderHeader = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const OrderTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const OrderNumber = styled.h2`
  font-size: 1.25rem;
  color: var(--text-color);
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 0.4rem 0.9rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  background-color: ${({ $status }) => {
    switch ($status) {
      case 'pending':    return '#FEF3C7';
      case 'preparing':  return '#DBEAFE';
      case 'ready':      return '#D1FAE5';
      case 'completed':  return '#F3F4F6';
      case 'cancelled':  return '#FEE2E2';
      default:           return '#F3F4F6';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'pending':    return '#92400E';
      case 'preparing':  return '#1E40AF';
      case 'ready':      return '#065F46';
      case 'completed':  return '#374151';
      case 'cancelled':  return '#991B1B';
      default:           return '#374151';
    }
  }};
`;

const CustomerName = styled.div`
  font-size: 1rem;
  color: var(--gray-dark);
  margin-top: 0.5rem;
`;

const OptionsNote = styled.div`
  font-size: 0.8rem;
  color: var(--gray-dark);
  margin-top: 0.15rem;
`;

const ItemsSection = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  color: var(--text-color);
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
`;

const TableHead = styled.thead`
  border-bottom: 2px solid var(--gray-light);

  th {
    padding: 0.6rem 0.5rem;
    text-align: left;
    color: var(--gray-dark);
    font-weight: 600;

    &:last-child {
      text-align: right;
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid var(--gray-light);

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: 0.75rem 0.5rem;
    color: var(--text-color);
    vertical-align: middle;

    &:last-child {
      text-align: right;
    }
  }
`;

const TotalSection = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 1.5rem 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-color);
`;

const LoadingState = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: var(--gray-dark);
  font-size: 1rem;
`;

const ErrorState = styled(LoadingState)`
  color: #991B1B;
  background-color: #FEF2F2;
`;

const BubbleTeaPickup: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [order, setOrder] = useState<BubbleTeaOrderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!token) {
        setError(t('bubbleTeaPickup.noTokenError'));
        setLoading(false);
        return;
      }

      try {
        const data = await bubbleTeaAPI.getOrderByPickupToken(token);
        setOrder(data);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setError(t('bubbleTeaPickup.orderNotFoundError'));
        } else {
          setError(t('bubbleTeaPickup.loadFailedError'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [token]);

  if (loading) {
    return (
      <Container>
        <LoadingState>{t('bubbleTeaPickup.loadingText')}</LoadingState>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container>
        <ErrorState>{error || t('bubbleTeaPickup.orderNotFoundError')}</ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>{t('bubbleTeaPickup.pageTitle')}</PageTitle>

      <OrderHeader>
        <OrderTitleRow>
          <OrderNumber>{t('bubbleTeaPickup.orderNumberLabel', { id: order.bt_order_id })}</OrderNumber>
          <StatusBadge $status={order.status}>
            {getStatusLabel(order.status, t)}
          </StatusBadge>
        </OrderTitleRow>
        {order.customer_name && (
          <CustomerName>{t('bubbleTeaPickup.orderForLabel', { name: order.customer_name })}</CustomerName>
        )}
      </OrderHeader>

      <ItemsSection>
        <SectionTitle>
          <FiPackage />
          {t('bubbleTeaPickup.itemsSectionTitle')}
        </SectionTitle>
        <ItemsTable>
          <TableHead>
            <tr>
              <th>{t('bubbleTeaPickup.itemHeader')}</th>
              <th>{t('bubbleTeaPickup.qtyHeader')}</th>
              <th>{t('bubbleTeaPickup.unitPriceHeader')}</th>
              <th>{t('bubbleTeaPickup.subtotalHeader')}</th>
            </tr>
          </TableHead>
          <TableBody>
            {order.items.map((item) => {
              const optionsExtra = item.subtotal - item.quantity * item.unit_price;
              const hasOptionsExtra = optionsExtra > 0.005;
              return (
                <tr key={item.bt_order_item_id}>
                  <td>{t('bubbleTeaPickup.itemFallback', { id: item.menu_item_id })}</td>
                  <td>{item.quantity}</td>
                  <td>{formatCurrency(item.unit_price)}</td>
                  <td>
                    {formatCurrency(item.subtotal)}
                    {hasOptionsExtra && (
                      <OptionsNote>{t('bubbleTeaPickup.optionsExtra', { amount: formatCurrency(optionsExtra) })}</OptionsNote>
                    )}
                  </td>
                </tr>
              );
            })}
          </TableBody>
        </ItemsTable>
      </ItemsSection>

      <TotalSection>
        <TotalRow>
          <span>{t('bubbleTeaPickup.totalLabel')}</span>
          <span>{formatCurrency(order.total)}</span>
        </TotalRow>
      </TotalSection>
    </Container>
  );
};

export default BubbleTeaPickup;
